import {
  BadRequestError,
  ForbiddenError,
  NetworkError,
  ServerError,
  TooManyRequestsError,
  UnauthorisedError
} from '@/errors/index.js'
import { useAuthStore } from '@/pinia/auth'
import alertbox from '@/utils/alertbox'
import { browserTracingIntegration, captureException, init } from '@sentry/vue'

window.addEventListener('vite:preloadError', async () => {
  await alertbox({
    title: 'New version available',
    text: 'A new version of the application is available. Please refresh the page to update.'
  })
  window.location.reload()
})

export default function (app, router, config) {
  const authStore = useAuthStore()
  const allowedEnvs = ['dev', 'test', 'staging', 'production'].includes(import.meta.env.MODE)
  const isAutotest = window.location.href.includes('autotest')
  if (allowedEnvs && !isAutotest) {
    init({
      app,
      dsn: 'https://4372a73ed9634417b01faf442f11a8f7@sentry.io/2524389',
      initialScope: {
        user: { id: authStore.id, email: authStore.email }
      },
      integrations: [browserTracingIntegration({ router })],
      environment: config.environment,
      release: import.meta.env.VITE_APP_GIT_COMMIT,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'Unable to preload CSS for'
      ],
      beforeSend(event, hint) {
        const originalException = hint?.originalException
        if (event.event_id && !window.logoutPush && originalException?.report !== false) {
          return event
        }
      }
    })
  }

  app.config.errorHandler = async function (err) {
    if (router.currentRoute.value.name === 'Error') return

    if (err instanceof BadRequestError || err instanceof ServerError) {
      router.replace({ name: 'Error', params: { eventId: err.eventId } })
    } else if (err instanceof UnauthorisedError) {
      router.replace({ path: '/logout', query: { path: err.redirectURL, redirect: router.currentRoute.value.path } })
    } else if (err instanceof NetworkError) {
      const { isConfirmed } = await alertbox({
        title: 'Network Error',
        text: "We couldn't connect to the server. Please check your internet connection and try again.",
        buttons: [{ text: 'Retry', style: 'btn-action', action: { isConfirmed: true } }]
      })
      if (isConfirmed) {
        window.location.reload()
      }
    } else if (err instanceof TooManyRequestsError) {
      await alertbox({
        title: 'Too Many Requests',
        text: "You've made too many requests. Please wait a moment and try again."
      })
    } else if (err instanceof ForbiddenError) {
      const result = await alertbox({
        title: 'You don’t have permission to access this resource.',
        text: 'If you believe this is a mistake, please try refreshing the page or log in again.',
        buttons: [
          { text: 'Refresh', style: 'btn-secondary', action: { isRefresh: true } },
          { text: 'Login', style: 'btn-action', action: { isConfirmed: true } }
        ]
      })
      if (result.isRefresh) {
        window.location.reload()
      } else if (result.isConfirmed) {
        router.replace({
          path: '/logout',
          query: { path: err.redirectURL, redirect: router.currentRoute.value.path }
        })
      }
    } else {
      const eventId = captureException(err)
      router.replace({
        name: 'Error',
        params: {
          eventId
        }
      })
    }
    console.error(err)
  }
}
